import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, input, output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { applyTransaction } from '@datorama/akita';
import { MessageService } from 'primeng/api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  skipWhile,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { LoanInfoService } from '../../../cmg-1003/components/loan-information/services/loan-info.service';
import {
  ARMQualRateOption,
  DpFactorType,
  FundingFeeType,
  IntOnlyQualPaymentCalcOption,
  LoanDataModel,
  LoanInfoCombinedHousingExpenseModel,
  LoanInfoRequest,
  LoanInfoRequestGvtFundingFactors,
  LoanPurposeType,
  MortgageType,
  PmiType,
  PurposeOfLoan,
  RefiTypeVA,
  StoreProperty,
  UpfrontMiType,
} from '../../../cmg-1003/models/loan-information.model';
import {
  aioStates,
  AmortizationType,
  amortizationTypeOptions,
  caseAssignmentDateOptions,
  cashOutRefinancePurposeOptions,
  constructionPurposeTypes,
  dpFactorOptions,
  fhaRefinancePurposeOptions,
  fundingFeeTypeOptions,
  intOnlyQualPaymentOptions,
  landLoanStatusTypes,
  loanProgramCodesVAForIRRR,
  mortgageAppliedForOptions,
  otherRefinancePurposeOptions,
  pmiTypeOptions,
  purposeOfLoanOptions,
  qualRateOptions,
  RefinancePurposeFha,
  RefinancePurposeVa,
  sectionOfActOptions,
  texasA6StatusOptions,
  upfrontMiTypeOptions,
  vaRefinancePurposeOptions,
} from '../../../cmg-1003/models/lookups';
import { states } from '../../../cmg-1003/models/lookups/common.lookups';
import { LoanProgram } from '../../../cmg-1003/models/lookups/loan-program.model';
import { OriginationChannel } from '../../../cmg-1003/models/origination-channel.enum';
import { Cmg1003SectionTypes } from '../../../cmg-1003/models/section-types.enum';
import { Cmg1003SubSectionTypes } from '../../../cmg-1003/models/sub-section-types.enum';
import { InvalidControlModel } from '../../../cmg-1003/models/validation-panel.model';
import { disableFormsBasedOnStatus, FeatureFlagRepository, SessionQuery, SessionStore } from '../../../core/state';
import { gusRunFirstDateOptions } from '../../../disclosure/models/lookups/loan-info.lookups';
import { LoanApplicationQuery } from '../../../disclosure/state/loan-application/loan-application.query';
import {
  AusQuery,
  Cmg1003FileDataQuery,
  Cmg1003FileDataStore,
  Cmg1003LoanQuery,
  Cmg1003LoanStore,
  Cmg1003LookupsQuery,
  Cmg1003PropertyQuery,
  Cmg1003PropertyStore,
  Cmg1003StoreQueryService,
} from '../../../loan-state';
import { LoanSummaryComponentActions } from '../../../loan-summary/components/common/loan-summary-component-actions';
import { LoanSummarySectionTypes } from '../../../loan-summary/models/loan-summary-section-types.enum';
import { unSupportedError } from '../../../loan-summary/utils/validation.utils';
import {
  canUseUnsavedChanges,
  checkIsNAN,
  findInvalidControls,
  formatDatePCalendar,
  formatNumber,
  isNullish,
  maxCurrencyValue,
  resetValidations,
  roundToValue,
} from '../../utils';
import { CmgValidators } from '../../utils/required.validator';
import { LoanStatus, LockStatusType } from '../../../cmg-1003/models';
import { SyncType } from '../../../models';
import { applyPartialEnablingOrDisablingForSecondOrThirdLien } from '../../../core/utils';
import { MiQuoteService } from '../../services/mi-quote.service';
import { Cmg1003LoanStatusHistoryQuery } from '../../../loan-state/loan-status-history/loan-status-history.query';
import { isGvtLoan } from '../../../cmg-1003/components/loan-information/utils/loan-info.utils';
import { Cmg1003Service } from '../../../cmg-1003/services/cmg-1003.service';
import { allowedBondLoanPrograms } from '../../models/loan/loan-allowed-bond';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { CmgValidatorMessagesComponent } from '../cmg-validator-messages/cmg-validator-messages.component';
import { DatePicker } from 'primeng/datepicker';
import { CmgInputErrorDirective } from '../../directives/cmg-input-error.directive';
import { CmgPrimeNgDatePickerMaskDirective } from '../../directives/cmg-datepicker-mask.directive';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../directives/cmg-input-number-focus.directive';
import { CmgDecimalInputComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-decimal-input/cmg-decimal-input.component';
import { CmgFormControlFactorResetDirective } from '../../directives/cmg-form-control-factor-reset.directive';
import { RadioButton } from 'primeng/radiobutton';
import { QaSlugPipe } from '../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-clear2-ui-loan-mortgage-type',
  templateUrl: './loan-mortgage-type.component.html',
  styleUrls: ['./loan-mortgage-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: LoanSummaryComponentActions, useExisting: LoanMortgageTypeComponent }],
  imports: [
    Message,
    ReactiveFormsModule,
    Select,
    CmgFormControlDomDirective,
    CmgValidatorMessagesComponent,
    NgTemplateOutlet,
    DatePicker,
    CmgInputErrorDirective,
    CmgPrimeNgDatePickerMaskDirective,
    Checkbox,
    InputText,
    Tooltip,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    CmgDecimalInputComponent,
    CmgFormControlFactorResetDirective,
    RadioButton,
    AsyncPipe,
    QaSlugPipe,
  ],
})
export class LoanMortgageTypeComponent implements OnInit, OnDestroy, LoanSummaryComponentActions {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _ausQuery = inject(AusQuery);
  private readonly _loanInfoService = inject(LoanInfoService);
  private readonly _loanStore = inject(Cmg1003LoanStore);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _lookupQuery = inject(Cmg1003LookupsQuery);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _fileDataStore = inject(Cmg1003FileDataStore);
  private readonly _propertyStore = inject(Cmg1003PropertyStore);
  private readonly _propertyQuery = inject(Cmg1003PropertyQuery);
  private readonly _loanApplicationQuery = inject(LoanApplicationQuery);
  private readonly _messageService = inject(MessageService);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _featureFlagRepository = inject(FeatureFlagRepository);
  private readonly _miQuoteService = inject(MiQuoteService);
  private readonly _cmg1003LoanStatusHistoryQuery = inject(Cmg1003LoanStatusHistoryQuery);
  private readonly _cmg1003Service = inject(Cmg1003Service);

  readonly loanSummary = input<boolean>(undefined);

  readonly pmiValueChange = output<PmiType>();

  loanInfo: LoanDataModel = new LoanDataModel();
  loanInfo$: Observable<LoanDataModel> = this._loanQuery.selectLoanInfo();
  pageLoad = false;
  mortgageTypeForm: UntypedFormGroup;
  firstRatio: number;
  secondRatio: number;
  monthlyMIFactorDefault = 0;
  upfrontMIAmountDefault = 0;
  upfrontMIFactorDefault = 0;
  maxBaseLoanAmount = maxCurrencyValue;

  //  Select Options
  states = states;
  refinancePurposeOptions = [];
  purposeOfLoanOptions = [];
  escrowsOptions = [
    {
      name: 'Select',
      value: 0,
    },
    {
      name: 'Waive All',
      value: 1,
    },
    {
      name: 'Not Waived',
      value: 2,
    },
    {
      name: 'Waive Taxes Only',
      value: 3,
    },
    {
      name: 'Waive Insurance Only',
      value: 4,
    },
  ];
  positiveRentalHistoryOptions = [
    {
      name: 'Yes',
      value: 1,
    },
    {
      name: 'No',
      value: 2,
    },
  ];
  fundingFeeOptions = fundingFeeTypeOptions;
  mortgageAppliedForOptions = mortgageAppliedForOptions;
  qualRateOptions = qualRateOptions;
  intOnlyQualPaymentOptions = intOnlyQualPaymentOptions;
  sectionOfActOptions = sectionOfActOptions;
  pmiTypeOptions = pmiTypeOptions;
  amortizationTypeOptions = amortizationTypeOptions;
  dpFactorOptions = dpFactorOptions;
  upfrontMiTypeOptions = upfrontMiTypeOptions;
  caseNumberAssignedDateOptions = caseAssignmentDateOptions;
  gusRunFirstDateOptions = gusRunFirstDateOptions;
  landLoanStatusOptions = landLoanStatusTypes;
  constructionPurposeOptions = constructionPurposeTypes;
  texasA6StatusOptions = texasA6StatusOptions;
  //  Observables
  loanProgramOptions$: Observable<LoanProgram[]> = EMPTY;
  loanProgramCodeOptions$: Observable<{ value: string; label: string }[]> = EMPTY;
  loanProgramNameOptions$: Observable<{ value: string; label: string }[]> = EMPTY;

  showAmortizationTypeDescription$ = this._loanQuery.showAmortizationTypeDescription$;
  showQualRateOption$ = this._loanQuery.showQualRateOption$;
  showPmiType$ = this._loanQuery.showPmiType$;
  showAfterImpValue$ = this._loanQuery.showAfterImpValue$;
  isFha$ = this._loanQuery.isFha$;
  isFhaRefi$ = this._loanQuery.isFhaRefi$;
  isFundingFee$ = this._loanQuery.isFundingFee$;
  isUSDA$ = this._loanQuery.isUSDA$;
  isOther$ = this._loanQuery.isOther$;
  isConventional$ = this._loanQuery.isConventional$;
  isHeloc$ = this._loanQuery.isHeloc$;
  showMonthlyMI$ = this._loanQuery.showMonthlyMI$;
  showMonthlyMIPaidInCash$ = this._loanQuery.showMonthlyMIPaidInCash$;
  showRefinancePurpose$ = this._loanQuery.showRefinancePurpose$;
  showRefinancePurposeCashOut$ = this._loanQuery.showRefinancePurposeCashOut$;
  showConstructionFields$ = this._loanQuery.showConstructionFields$;
  showOtherPurposeDescription$ = this._loanQuery.showOtherPurposeDescription$;
  showRateChange$ = this._loanQuery.showRateChange$;
  hasSoldRealEstateIn60Months$ = this._loanQuery.hasSoldRealEstateIn60Months$;
  showDownPaymentPercentage$ = this._loanQuery.showDownPaymentPercentage$;
  showDownPaymentAmount$ = this._loanQuery.showDownPaymentAmount$;
  showHomeOwnedThroughVa$ = this._loanQuery.showHomeOwnedThroughVa$;
  refinancePurposeCashOutOptions = cashOutRefinancePurposeOptions;
  isMiQuoteDisabled = true;

  is2ndG01LoanProgram$ = new BehaviorSubject(false);
  disableMinimumDrawAmount$ = new BehaviorSubject(false);

  // ========= Start: [URLA2-1376] - TEMPORARY. ========= //
  loanProgramError = {
    message: 'Program not supported in Clear 2.0',
    showingBanner: false,
    messageServiceKey: 'loan-program-error',
  };
  loanProgramOptions: LoanProgram[] = [];
  originalLoanProgram = { loanProgramCode: '', loanProgramName: '' };
  // ========= End: [URLA2-1376] - TEMPORARY. ========= //

  AmortizationType = AmortizationType;
  positiveRentalHistoryTooltipText = '';

  appraisalValueWarning = '';
  showAppraisalValueWarning = false;
  showAppraisalValueHelper = false;
  showTexasA6Status = false;

  private _prevPmiTypeValue: PmiType;
  private _canEmitPmiValueChange = true;

  private readonly _destroyed$ = new Subject<boolean>();
  private _isFullyDisabled = false;
  private _isHomeFundItLoan = false;
  private _wasCalculateLoanAlreadyCalled = false;
  private _isTexasState = false;
  private _syncType: SyncType = SyncType.None;
  private _originationChannel: OriginationChannel;

  ngOnInit(): void {
    this.mortgageTypeForm = this._fb.group(
      {
        mortgageAppliedFor: ['', [CmgValidators.required(0)]],
        armQualRateOption: ['', [CmgValidators.required(0)]],
        intOnlyQualPaymentCalcOption: ['', [CmgValidators.required(0)]],
        loanProgramCode: [
          '',
          [
            Validators.required,
            CmgValidators.callbackValidation(this.loanProgramError.message, (control: AbstractControl) =>
              this._supportedLoanProgramValidator('loanProgramCode', control),
            ),
          ],
        ],
        loanProgramName: [
          '',
          [
            Validators.required,
            CmgValidators.callbackValidation(this.loanProgramError.message, (control: AbstractControl) =>
              this._supportedLoanProgramValidator('loanProgramName', control),
            ),
          ],
        ],
        loanPurpose: [0, [CmgValidators.required(0)]],
        waiveEscrow: [0],
        otherPurposeDescription: [''],
        interestOnlyPeriod: [{ value: '', disabled: true }],
        appraisedValue: [null, { updateOn: 'blur' }],
        salesPrice: [null, [CmgValidators.required(0)], null, { updateOn: 'blur' }],
        maxLoanLimitByCounty: [{ value: 0, disabled: true }],
        dpfactor: [''],
        baseLoanAmount: [0, [CmgValidators.required(0)], null, { updateOn: 'blur' }],
        intRate: [''],
        qualRate: [{ value: null, disabled: true }],
        loanTerm: [360, [CmgValidators.required(0)]],
        balloonTerm: [{ value: null, disabled: true }], // readonly field
        agencyCaseNumber: [{ value: '', disabled: true }],
        lenderCaseNumber: [{ value: '', disabled: true }],
        afterImprovedValue: [0],
        armIntRateFixedFor: [{ value: 0, disabled: true }],
        armIntRateAdjustAt: [{ value: 0, disabled: true }],
        armMargin: [{ value: 0, disabled: true }],
        armIndexValue: [{ value: 0, disabled: true }],
        armIndexValueForWeekEnding: [{ value: '', disabled: true }],
        armLifeCap: [null],
        totalLoanAmount: [{ value: null, disabled: true }],
        interestRate: [null, [CmgValidators.required(0)]],
        caseNumberAssignedDate: [15],
        gusFirstRunDate: [6],
        amortizationType: [{ value: 0, disabled: true }],
        isNegativeAmortization: [false],
        isGrantLoan: [false],
        amortizationTypeDescription: [{ value: '', disabled: true }],
        appraisalValue: [null, [CmgValidators.required(0)]],
        downPaymentPercentage: [0, { updateOn: 'blur' }],
        downPaymentAmount: [0, { updateOn: 'blur' }],

        //  VA/FHA
        lenderFhaId: [{ value: '', disabled: true }],
        lenderVaId: [{ value: '', disabled: true }],
        sponsorVaid: [''],
        sponsorFhaid: [''],
        fundingFeeType: [0, CmgValidators.required(0)],

        //  Conventional/Other
        pmiType: [{ value: PmiType.none }],

        //  VA
        sectionOfAct: [0, CmgValidators.required(0)],
        areYouDisabledVeteran: [false, Validators.required],
        homeOwnedThroughVa: [false, Validators.required],
        refinancePurposeVa: [0, CmgValidators.required(0)],
        //  FHA
        haveYouSoldRealEstateIn60Months: [false, Validators.required],
        positiveRentalHistory: [2, CmgValidators.required(0)],
        isDwellingToBeCovered: [false, Validators.required],
        dwellingCoveredDetails: ['', Validators.required],
        doYouOwnMoreThan4Dwelling: [false, Validators.required],
        refinancePurposeFha: [0, CmgValidators.required(0)],
        isItToBeSold: [false],
        isHomeFundItLoan: [false],
        soldSalesPriceFha: [0],
        originalMortgageAmount: [0],
        fhaExpectedEndorsementDate: [null, Validators.required],
        fhaPreviousEndorsementDate: [null, Validators.required],
        //  USDA
        householdMembers: [[]],
        //  Au
        refinancePurposeAu: [0, CmgValidators.required(0)],
        //  Conventional
        isJumbo: [{ value: false, disabled: true }],
        //  Other
        otherMortgageDescription: [''],

        //  PMI Monthly
        monthlyMiFactor: [{ value: '', disabled: true }], // disabled for FHA/USDA
        monthlyMiAmount: [{ value: '', disabled: true }], // disabled for FHA/USDA/HELOC
        //  PMI Standard
        miPaidInCash: [{ value: '', disabled: true }],
        upfrontMiFactor: [{ value: '', disabled: true }], // disabled for FHA/USDA
        upfrontMiAmount: [{ value: '', disabled: true }],
        mipffFinancingCalcOption: [0],
        // HELOC
        minimumDrawAmount: [null],
        aioInitialDrawAmount: [null],
        // Construction & constrution Perm
        landLoanStatus: [0],
        constructionPurpose: [0],
        // Texas A6 fields
        texasA6Status: [0],
      },
      { updateOn: 'change' },
    );
    this._setupObservables();
    this._awaitAutoRecoveryDecision();

    disableFormsBasedOnStatus([this.mortgageTypeForm], this._storeQuery, {
      controlsToPreserve: ['refinancePurposeVa', 'refinancePurposeFha', 'refinancePurposeAu'],
    });

    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        debounceTime(500),
        take(1),
      )
      .subscribe(() => {
        this.mortgageTypeForm.statusChanges.pipe(debounceTime(0)).subscribe(() => {
          this._sessionStore.triggerRevalidation();
        });
      });

    this._miQuoteService.resetPmiType$.pipe(takeUntil(this._destroyed$)).subscribe((value) => {
      if (value) {
        if (this._prevPmiTypeValue !== null && this._prevPmiTypeValue !== undefined) {
          this._canEmitPmiValueChange = false;
          this.mortgageTypeForm.get('pmiType').setValue(this._prevPmiTypeValue);
          this._handlePmiTypeChange();
          if (!this.isMiQuoteDisabled) {
            this.mortgageTypeForm.get('pmiType').markAsTouched({ onlySelf: true });
            this.mortgageTypeForm.get('pmiType').updateValueAndValidity({ onlySelf: true });
          }
          this._canEmitPmiValueChange = true;
        }
      }
    });

    this._setMortgageAppliedForDisabledState();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  get isAio() {
    return this.loanInfo?.mortgageAppliedFor === MortgageType.heloc && this.loanInfo?.loanProgramCode?.startsWith('801');
  }

  get isHeloc() {
    return this.loanInfo?.mortgageAppliedFor === MortgageType.heloc && !this.loanInfo?.loanProgramCode?.startsWith('801');
  }

  updateSalesPrice(): void {
    this._loanStore.salesPriceUpdate(this.mortgageTypeForm.get('salesPrice').value);
  }

  getSectionType(): LoanSummarySectionTypes {
    return LoanSummarySectionTypes.loanInformation;
  }

  validate(): void {
    CmgUiUtilities.markFormAsDirty(this.mortgageTypeForm);
    CmgUiUtilities.markFormAsTouched(this.mortgageTypeForm);

    const assembleErrorMessage = (key: string, controlError: InvalidControlModel) => {
      switch (key) {
        case 'interestRate':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Interest Rate - Required';
          }
          break;
        case 'baseLoanAmount':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Base Loan Amount - Required';
          }
          break;
        case 'appraisalValue':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Appraisal Value - Required';
          }
          break;
        case 'loanPurpose':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Purpose of Loan - Required';
          } else if (controlError.errors['cmgValidationError']) {
            controlError.invalidMessage = controlError.errors['cmgValidationError'];
            controlError.action = unSupportedError;
          }
          break;
        case 'salesPrice':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Sales Price / Estimated Value - Required';
          }
          break;
        case 'loanTerm':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Loan Term - Required';
          }
          break;
        case 'mortgageAppliedFor':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Mortgage Type - Required';
          }
          break;
        case 'loanProgramCode':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Loan Program Code - Required';
          }
          break;
        case 'loanProgramName':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Loan Program Description - Required';
          }
          break;
        case 'fundingFeeType':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Funding Fee Type - Required';
          }
          break;
        case 'sectionOfAct':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Section of Act - Required';
          }
          break;
        case 'dwellingCoveredDetails':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Dwelling Covered Details - Required';
          }
          break;
        case 'refinancePurposeFha':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Refinance Type - Required';
          }
          break;
        case 'refinancePurposeVa':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Refinance Type - Required';
          }
          break;
        case 'refinancePurposeAu':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Refinance Type - Required';
          }
          break;
        case 'minimumDrawAmount':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Minimum Draw Amount - Required';
            controlError.excludeFromAus = true; // This validation is needed to act as a hard stop only. Do not show in AUS panel.
          }
          break;
        default:
          break;
      }
    };

    this._sessionStore.setValidationError(
      Cmg1003SectionTypes.loanInformation,
      findInvalidControls(this.mortgageTypeForm, assembleErrorMessage),
      Cmg1003SubSectionTypes.loanMortgageType,
    );

    // This ensures validation styling is applied after everything is checked.
    this._cdRef.detectChanges();
  }

  resetValidations(): void {
    this.clearLoanProgramError();

    resetValidations(this.mortgageTypeForm);
    setTimeout(() => {
      this._cdRef.detectChanges();
    }, 100);
  }

  clearLoanProgramError() {
    this._messageService.clear(this.loanProgramError.messageServiceKey);
    this.loanProgramError.showingBanner = false;
  }

  onPmiTypeChange(event: any): void {
    this._handlePmiTypeChange();
    if (event.value !== PmiType.select && event.value !== PmiType.none && event.value !== PmiType.lenderPaid && !this.isMiQuoteDisabled) {
      if (this._canEmitPmiValueChange) {
        this.pmiValueChange.emit(event.value);
      }
    }
  }

  handleAppraisalValueWarning() {
    const appraisalValue = this.mortgageTypeForm.get('appraisalValue').value;
    const salesPrice = this.mortgageTypeForm.get('salesPrice').value;
    if (appraisalValue !== null && appraisalValue !== undefined) {
      if (appraisalValue < salesPrice) {
        this.appraisalValueWarning = 'Appraisal Value is lower than Sales Price / Estimated Value.';
        this.showAppraisalValueWarning = true;
        this.showAppraisalValueHelper = true;
      } else if (appraisalValue > salesPrice) {
        this.appraisalValueWarning = '';
        this.showAppraisalValueWarning = false;
        this.showAppraisalValueHelper = true;
      } else if (appraisalValue === salesPrice) {
        this.appraisalValueWarning = '';
        this.showAppraisalValueWarning = false;
        this.showAppraisalValueHelper = false;
      }
    } else {
      this.appraisalValueWarning = '';
      this.showAppraisalValueWarning = false;
      this.showAppraisalValueHelper = false;
    }
    this._cdRef.detectChanges();
  }

  private _calculateLoan(loan: LoanDataModel): void {
    const request = new LoanInfoRequest();
    request.baseLoan = loan.baseLoanAmount;
    request.downPayment = checkIsNAN(loan.downPaymentAmount);
    request.downPaymentPercent = checkIsNAN(loan.downPaymentPercentage);
    request.salesPrice = checkIsNAN(loan.salesPrice);
    request.dpFactor = loan.dpfactor;
    request.mortgageType = loan.mortgageAppliedFor;
    request.loanProgramCode = loan.loanProgramCode;
    request.fundingFeeType = loan.fundingFeeType;
    request.loanPurpose = loan.loanPurpose?.toString();
    request.mipffFinancingCalcOption = loan.mipffFinancingCalcOption;

    if (request.occupancyType === 2) {
      request.PresentCombinedHousingExpenses = checkIsNAN(request.PresentCombinedHousingExpenses);
    }

    const firstMortgageProposed = checkIsNAN(loan.loanInfoCombinedHousingExpense?.firstMortgageProposed),
      mortgageInsuranceProposed = checkIsNAN(loan.loanInfoCombinedHousingExpense?.mortgageInsuranceProposed),
      proposedTotal = checkIsNAN(loan.loanInfoCombinedHousingExpense?.proposedTotal);
    request.proposedCombinedHousingExpenses = proposedTotal - firstMortgageProposed - mortgageInsuranceProposed;
    request.monthlyMCC = loan.monthlyMCC;
    request.grossMonthlyIncome = loan.loanInfoCombinedHousingExpense?.grossTotalIncome;
    request.totalMonthlyPayment = loan.totalMOnthlyPayments;
    request.interestRate = loan.interestRate;
    request.loanTerm = loan.loanTerm;
    request.balloonTerm = loan.balloonTerm;
    request.appraisalValue = loan.appraisalValue;
    request.hellocCreditLimit =
      loan.subOrdinateFinance?.subFiType === 1
        ? checkIsNAN(loan.subOrdinateFinance?.helocMaxBalance)
        : checkIsNAN(loan.subOrdinateFinance?.subFiBaseLoan);
    request.subordinateFinanceAmount = checkIsNAN(loan.subOrdinateFinance?.subFiBaseLoan);
    request.fhaCaseNoAssigned = request.mortgageType === 2 ? loan.caseNumberAssignedDate : loan.gusFirstRunDate;
    request.monthlyMiFactor = loan.monthlyMiFactor;
    request.upfrontMiFactor = loan.upfrontMiFactor;
    if (!isNullish(request.interestRate) && request.interestRate.toString() === '') {
      request.interestRate = null;
    }
    const result = this._loanInfoService.calculateLoan(request);
    const combinedHousingExpense = new LoanInfoCombinedHousingExpenseModel();
    combinedHousingExpense.proposedTotal = result.piti;
    combinedHousingExpense.firstMortgageProposed = result.firstMortgagePI;
    combinedHousingExpense.mortgageInsuranceProposed = result.monthlyMiAmount;
    //  Update Store with new calculated values
    applyTransaction(() => {
      this._updateStoreAndForm(StoreProperty.baseLoanAmount, result.baseLoan);
      this._updateStoreAndForm(StoreProperty.upfrontMiAmount, result.upfrontMiAmount);
      this._updateStoreAndForm(StoreProperty.upfrontMiFactor, result.upfrontMiFactor);
      this._updateStoreAndForm(StoreProperty.miPaidInCash, result.mipPaidInCash);
      this._updateStoreAndForm(StoreProperty.monthlyMiFactor, result.monthlyMiFactor);
      this._updateStoreAndForm(StoreProperty.monthlyMiAmount, result.monthlyMiAmount);
      this._updateStoreAndForm(StoreProperty.totalLoanAmount, result.noteLoanAmount);
    });
    //  Not in form ui
    const subOrdinateFinance = Object.assign({}, loan.subOrdinateFinance);

    // If loan.subOrdinateFinance?.helocMaxBalance is null, result.hcltv becomes null (see _loanInfoService.calculateLoan(request)).
    // In that case, use loan.subOrdinateFinance?.hcltv as a fallback, in order to match Clear 1 behavior.
    if (subOrdinateFinance) {
      subOrdinateFinance.hcltv = result.hcltv || loan.subOrdinateFinance?.hcltv;
    }

    this._loanStore.update({
      loanInfoCombinedHousingExpense: combinedHousingExpense,
      subOrdinateFinance: subOrdinateFinance,
      ltv: loan.ltv,
      cltv: loan.cltv,
    });

    if (!this._wasCalculateLoanAlreadyCalled) {
      this._wasCalculateLoanAlreadyCalled = true;
      this._loanQuery.dirtyChecker2.dirtyChecker.reset();
    }
  }

  private _calculateLoanGvtFundingFactors(loan: LoanDataModel): void {
    const request = new LoanInfoRequestGvtFundingFactors();
    request.baseLoan = loan.baseLoanAmount;
    request.downPayment = checkIsNAN(loan.downPaymentAmount);
    request.downPaymentPercent = checkIsNAN(loan.downPaymentPercentage);
    request.salesPrice = checkIsNAN(loan.salesPrice);
    request.dpFactor = loan.dpfactor;
    request.mortgageType = loan.mortgageAppliedFor;
    request.loanProgramCode = loan.loanProgramCode;
    request.fundingFeeType = loan.fundingFeeType;
    request.loanPurpose = loan.loanPurpose?.toString();
    request.mipffFinancingCalcOption = loan.mipffFinancingCalcOption;

    if (request.occupancyType === 2) {
      request.PresentCombinedHousingExpenses = checkIsNAN(request.PresentCombinedHousingExpenses);
    }

    const firstMortgageProposed = checkIsNAN(loan.loanInfoCombinedHousingExpense?.firstMortgageProposed),
      mortgageInsuranceProposed = checkIsNAN(loan.loanInfoCombinedHousingExpense?.mortgageInsuranceProposed),
      proposedTotal = checkIsNAN(loan.loanInfoCombinedHousingExpense?.proposedTotal);
    request.proposedCombinedHousingExpenses = proposedTotal - firstMortgageProposed - mortgageInsuranceProposed;
    request.monthlyMCC = loan.monthlyMCC;
    request.grossMonthlyIncome = loan.loanInfoCombinedHousingExpense?.grossTotalIncome;
    request.totalMonthlyPayment = loan.totalMOnthlyPayments;
    request.interestRate = loan.interestRate;
    request.loanTerm = loan.loanTerm;
    request.balloonTerm = loan.balloonTerm;
    request.appraisalValue = loan.appraisalValue;
    request.hellocCreditLimit =
      loan.subOrdinateFinance?.subFiType === 1
        ? checkIsNAN(loan.subOrdinateFinance?.helocMaxBalance)
        : checkIsNAN(loan.subOrdinateFinance?.subFiBaseLoan);
    request.subordinateFinanceAmount = checkIsNAN(loan.subOrdinateFinance?.subFiBaseLoan);
    request.fhaCaseNoAssigned = request.mortgageType === 2 ? loan.caseNumberAssignedDate : loan.gusFirstRunDate;
    request.monthlyMiFactor = loan.monthlyMiFactor;
    request.upfrontMiFactor = loan.upfrontMiFactor;
    if (!isNullish(request.interestRate) && request.interestRate.toString() === '') {
      request.interestRate = null;
    }
    const result = this._loanInfoService.calculateLoanGvtFundingFactors(request);
    const combinedHousingExpense = new LoanInfoCombinedHousingExpenseModel();
    combinedHousingExpense.proposedTotal = result.piti;
    combinedHousingExpense.firstMortgageProposed = result.firstMortgagePI;
    combinedHousingExpense.mortgageInsuranceProposed = loan.monthlyMiAmount;
    //  Update Store with new calculated values
    applyTransaction(() => {
      this._updateStoreAndForm(StoreProperty.baseLoanAmount, result.baseLoan);
    });
    //  Not in form ui
    const subOrdinateFinance = Object.assign({}, loan.subOrdinateFinance);

    // If loan.subOrdinateFinance?.helocMaxBalance is null, result.hcltv becomes null (see _loanInfoService.calculateLoan(request)).
    // In that case, use loan.subOrdinateFinance?.hcltv as a fallback, in order to match Clear 1 behavior.
    if (subOrdinateFinance) {
      subOrdinateFinance.hcltv = result.hcltv || loan.subOrdinateFinance?.hcltv;
    }

    this._loanStore.update({
      loanInfoCombinedHousingExpense: combinedHousingExpense,
      subOrdinateFinance: subOrdinateFinance,
      ltv: loan.ltv,
      cltv: loan.cltv,
    });

    if (!this._wasCalculateLoanAlreadyCalled) {
      this._wasCalculateLoanAlreadyCalled = true;
      this._loanQuery.dirtyChecker2.dirtyChecker.reset();
    }
  }

  private _disableBaseLoanAmount(): void {
    const isDisabled = this.loanInfo.dpfactor === DpFactorType.downpaymentPercentage || this.loanInfo.dpfactor === 'Downpayment Amount ($)';
    if (this._isFullyDisabled || isDisabled) {
      this.mortgageTypeForm.get('baseLoanAmount').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('baseLoanAmount').enable({ emitEvent: false });
    }
  }

  private _disableControls() {
    this._fileDataQuery
      .select((f) => f.syncType)
      .pipe(take(1))
      .subscribe((syncType) => {
        this._syncType = syncType;
        this._disableFactor();
        this._disableBaseLoanAmount();
        this._disableQualRate();

        this._disableMonthlyMiFactor();

        this._disableUpfrontMiType();
        this._disableUpfrontMiFactor();
        this._disableDwellingCoveredDetails();
        this._disableHiddenRefinance();
        this._disableHiddenSectionOfAct();
        this._disableHelocFields();
        this._disableQualRateOptionField();
        this._applyPartialEnablingIfSecondOrThirdLien();
      });
  }

  private _applyPartialEnablingIfSecondOrThirdLien() {
    if (this._syncType !== SyncType.None) {
      const isSecondOrThirdLien = this._syncType === SyncType.SecondLien || this._syncType === SyncType.ThirdLien;
      const mortgageType = this.mortgageTypeForm.controls.mortgageAppliedFor.value;
      let fieldsToEnable: string[] = [];

      if (isSecondOrThirdLien) {
        this.mortgageTypeForm.disable({ emitEvent: false });
        switch (mortgageType) {
          case 0:
          case MortgageType.fha:
          case MortgageType.va:
          case MortgageType.usda:
            fieldsToEnable = ['mortgageAppliedFor'];
            break;
          case MortgageType.conventional:
            fieldsToEnable = [
              'mortgageAppliedFor',
              'loanProgramCode',
              'loanProgramName',
              'loanPurpose',
              'loanTerm',
              'interestRate',
              'dpfactor',
              'baseLoanAmount',
            ];
            break;
          case MortgageType.heloc: {
            fieldsToEnable = [
              'aioInitialDrawAmount',
              'armLifeCap',
              'armQualRateOption',
              'baseLoanAmount',
              'dpfactor',
              'intOnlyQualPaymentCalcOption',
              'interestRate',
              'loanProgramCode',
              'loanProgramName',
              'loanPurpose',
              'loanTerm',
              'mortgageAppliedFor',
              'waiveEscrow',
            ];
            const loanProgramCode = this.mortgageTypeForm.controls.loanProgramCode.value;
            if (loanProgramCode.startsWith('801')) {
              const fieldsToDisable = fieldsToEnable.filter((f) => f !== 'mortgageAppliedFor' && f !== 'loanProgramCode');
              applyPartialEnablingOrDisablingForSecondOrThirdLien(
                false,
                fieldsToDisable,
                this.mortgageTypeForm,
                this._sessionQuery,
                this._fileDataQuery,
              );
              fieldsToEnable = fieldsToEnable.filter((f) => f === 'mortgageAppliedFor' || f === 'loanProgramCode');
            }
            break;
          }
          case MortgageType.other:
            fieldsToEnable = [
              'baseLoanAmount',
              'dpfactor',
              'interestRate',
              'loanProgramCode',
              'loanProgramName',
              'loanPurpose',
              'mortgageAppliedFor',
              'otherMortgageDescription',
              'waiveEscrow',
            ];
            break;
          case MortgageType.stateAgency: {
            fieldsToEnable = [
              'baseLoanAmount',
              'dpfactor',
              'interestRate',
              'loanProgramCode',
              'loanProgramName',
              'loanPurpose',
              'loanTerm',
              'mortgageAppliedFor',
              'waiveEscrow',
              'qualRate',
              'isNegativeAmortization',
              'isGrantLoan',
            ];
            const amortizationType = this.mortgageTypeForm.controls.amortizationType.value;
            if (amortizationType === AmortizationType.Fixed) {
              applyPartialEnablingOrDisablingForSecondOrThirdLien(
                false,
                ['qualRate'],
                this.mortgageTypeForm,
                this._sessionQuery,
                this._fileDataQuery,
              );
              fieldsToEnable = fieldsToEnable.filter((f) => f !== 'qualRate');
            } else if (amortizationType === '') {
              fieldsToEnable = fieldsToEnable.filter((f) => f === 'mortgageAppliedFor' || f === 'loanProgramCode');
            }
            break;
          }
        }

        if (fieldsToEnable.length > 0) {
          setTimeout(() => {
            applyPartialEnablingOrDisablingForSecondOrThirdLien(true, fieldsToEnable, this.mortgageTypeForm, this._sessionQuery, this._fileDataQuery);
          }, 0);
        }
      }
    }
  }

  private _disableQualRateOptionField() {
    if (this._isFullyDisabled || this.isAio) {
      this.mortgageTypeForm.get('armQualRateOption').disable({ emitEvent: false });
      this.mortgageTypeForm.get('intOnlyQualPaymentCalcOption').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('armQualRateOption').enable({ emitEvent: false });
      this.mortgageTypeForm.get('intOnlyQualPaymentCalcOption').enable({ emitEvent: false });
    }
  }

  private _disableHelocFields(): void {
    if (this.loanInfo.mortgageAppliedFor === MortgageType.heloc) {
      const isDisabledHeloc =
        this.loanInfo?.loanProgramCode?.startsWith('801') || !this.loanInfo?.loanProgramCode || this.loanInfo?.loanProgramCode === 'Select';

      if (this._isFullyDisabled || isDisabledHeloc) {
        this.mortgageTypeForm.get('aioInitialDrawAmount').disable({ emitEvent: false });
      } else {
        this.mortgageTypeForm.get('aioInitialDrawAmount').enable({ emitEvent: false });
      }
    }
  }

  private _disableDwellingCoveredDetails(): void {
    if (this._isFullyDisabled || !this.loanInfo.isDwellingToBeCovered) {
      this.mortgageTypeForm.get('dwellingCoveredDetails').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('dwellingCoveredDetails').enable({ emitEvent: false });
    }
  }

  private _disableFactor(): void {
    let disableFactor: boolean;
    if (!isNullish(this.loanInfo.lockStartDate)) {
      if (isNullish(this.loanInfo.lockExpirationDate)) {
        disableFactor = true;
      } else {
        const expdate: Date = new Date(this.loanInfo.lockExpirationDate.toString()),
          todaysdate: Date = new Date();
        if (expdate.getTime() >= todaysdate.getTime()) {
          disableFactor = true;
        }
      }
    }

    if (!isNullish(this.loanInfo.inProcessingDate)) {
      disableFactor = true;
    }

    if (this._isFullyDisabled || disableFactor) {
      this.mortgageTypeForm.get('dpfactor').disable({ emitEvent: false });
      this.mortgageTypeForm.get('downPaymentPercentage').disable({ emitEvent: false });
      this.mortgageTypeForm.get('downPaymentAmount').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('dpfactor').enable({ emitEvent: false });
      this.mortgageTypeForm.get('downPaymentPercentage').enable({ emitEvent: false });
      this.mortgageTypeForm.get('downPaymentAmount').enable({ emitEvent: false });
    }
  }

  private _disableHiddenRefinance() {
    /**
     * Only one of these three formControls will display at once or none at all
     * therefore, we need to enable and disable so that the required CmgValidators.required
     * don't trigger for hidden fields
     */
    if (this.loanInfo.loanPurpose === LoanPurposeType.refinance) {
      switch (this.loanInfo.mortgageAppliedFor) {
        case MortgageType.va: {
          const refinancePurposeVaValue = this.mortgageTypeForm.get('refinancePurposeVa').value;
          this.mortgageTypeForm.get('refinancePurposeVa').enable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeFha').disable({ emitEvent: false });
          refinancePurposeVaValue === RefinancePurposeVa.Other
            ? this.mortgageTypeForm.get('refinancePurposeAu').enable({ emitEvent: false })
            : this.mortgageTypeForm.get('refinancePurposeAu').disable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeVa').markAsPristine();
          this.mortgageTypeForm.get('refinancePurposeVa').markAsUntouched();
          break;
        }
        case MortgageType.fha: {
          const refinancePurposeFhaValue = this.mortgageTypeForm.get('refinancePurposeFha').value;
          this.mortgageTypeForm.get('refinancePurposeVa').disable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeFha').enable({ emitEvent: false });
          refinancePurposeFhaValue === RefinancePurposeFha.CashOut
            ? this.mortgageTypeForm.get('refinancePurposeAu').enable({ emitEvent: false })
            : this.mortgageTypeForm.get('refinancePurposeAu').disable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeFha').markAsPristine();
          this.mortgageTypeForm.get('refinancePurposeFha').markAsUntouched();
          break;
        }
        default:
          this.mortgageTypeForm.get('refinancePurposeVa').disable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeFha').disable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeAu').enable({ emitEvent: false });
          this.mortgageTypeForm.get('refinancePurposeAu').markAsPristine();
          this.mortgageTypeForm.get('refinancePurposeAu').markAsUntouched();
          break;
      }
    } else {
      this.mortgageTypeForm.get('refinancePurposeVa').disable({ emitEvent: false });
      this.mortgageTypeForm.get('refinancePurposeFha').disable({ emitEvent: false });
      this.mortgageTypeForm.get('refinancePurposeAu').disable({ emitEvent: false });
    }
  }

  private _disableHiddenSectionOfAct(): void {
    if (this._isFullyDisabled || this.loanInfo.mortgageAppliedFor !== MortgageType.fha) {
      this.mortgageTypeForm.get('sectionOfAct').disable({ emitEvent: false });
      this.mortgageTypeForm.get('positiveRentalHistory').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('sectionOfAct').enable({ emitEvent: false });
      this.mortgageTypeForm.get('sectionOfAct').markAsPristine();
      this.mortgageTypeForm.get('sectionOfAct').markAsUntouched();
      this.mortgageTypeForm.get('positiveRentalHistory').enable({ emitEvent: false });
      this.mortgageTypeForm.get('positiveRentalHistory').markAsPristine();
      this.mortgageTypeForm.get('positiveRentalHistory').markAsUntouched();
    }
  }

  private _disableMonthlyMiFactor(): void {
    if (
      this._isFullyDisabled ||
      this.isAio ||
      (this.loanInfo.mortgageAppliedFor !== MortgageType.other &&
        this.loanInfo.mortgageAppliedFor !== MortgageType.conventional &&
        this.loanInfo.mortgageAppliedFor !== MortgageType.heloc) ||
      this._syncType === SyncType.SecondLien ||
      this._syncType === SyncType.ThirdLien
    ) {
      this.mortgageTypeForm.get('monthlyMiFactor').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('monthlyMiFactor').enable({ emitEvent: false });
    }
  }

  private _disableQualRate(): void {
    if (
      this._isFullyDisabled ||
      this.loanInfo.amortizationType === AmortizationType.Fixed ||
      this.loanInfo.amortizationType === AmortizationType.ARM ||
      this.isAio
    ) {
      this.mortgageTypeForm.get('qualRate').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('qualRate').enable({ emitEvent: false });
    }
  }

  private _disableUpfrontMiFactor(): void {
    const enabledMortgageTypes = [MortgageType.other, MortgageType.conventional, MortgageType.heloc];

    if (this._isFullyDisabled || this.isAio || !enabledMortgageTypes.includes(this.loanInfo.mortgageAppliedFor)) {
      this.mortgageTypeForm.get('upfrontMiFactor').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('upfrontMiFactor').enable({ emitEvent: false });
    }
  }

  private _disableUpfrontMiType(): void {
    const enabledMortgageTypes = [MortgageType.other, MortgageType.conventional, MortgageType.heloc, MortgageType.fha, MortgageType.usda];
    if (this._isFullyDisabled || this.isAio || !enabledMortgageTypes.includes(this.loanInfo.mortgageAppliedFor)) {
      this.mortgageTypeForm.get('mipffFinancingCalcOption').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('mipffFinancingCalcOption').enable({ emitEvent: false });
    }
  }

  private _hasLoanProgramCodeForIRRR(): boolean {
    const containsLoanProgramCode = (code) => code === this.loanInfo.loanProgramCode;
    return loanProgramCodesVAForIRRR.some(containsLoanProgramCode);
  }

  //Initialize method for dropdown change
  private _initialize(mortgageType: MortgageType): void {
    const caseNum = mortgageType === MortgageType.fha ? 15 : this.loanInfo.caseNumberAssignedDate;
    const gusFirstRunDate = mortgageType === MortgageType.fha ? 3 : this.loanInfo.gusFirstRunDate;
    applyTransaction(() => {
      this._updateStoreAndForm(StoreProperty.caseNumberAssignedDate, caseNum);
      this._updateStoreAndForm(StoreProperty.rhsCaseNoAssignmentPeriod, gusFirstRunDate);
    });
    // TODO figure out if this method is needed
    // this.SetAIO(true);
    this._setMIFields(mortgageType);
    this._populatePurposeOfRefinanceDropdown(1, this.loanInfo.loanPurpose, mortgageType);
    this._populatePurposeOfLoanDropdown(mortgageType);
    if (mortgageType === MortgageType.fha || mortgageType === MortgageType.heloc || mortgageType === MortgageType.other) {
      this._updateStoreAndForm(StoreProperty.pmiType, PmiType.none);
    }
  }

  private _onChangeLoanProgram(): void {
    this._updateStoreAndForm(StoreProperty.qualRate, this._calculateQualRate(), false);
  }

  private _checkBondLoanProgramName(loanProgramName: string) {
    const loanProgramNameLowerCase = loanProgramName?.toLowerCase();
    if (loanProgramNameLowerCase?.includes('bond') && !allowedBondLoanPrograms.includes(loanProgramNameLowerCase)) {
      this.mortgageTypeForm.get('isHomeFundItLoan').setValue(false, { emitEvent: false });
      this.mortgageTypeForm.get('isHomeFundItLoan').disable({ emitEvent: false });
    } else {
      this.mortgageTypeForm.get('isHomeFundItLoan').setValue(this._isHomeFundItLoan, { emitEvent: false });
      this.mortgageTypeForm.get('isHomeFundItLoan').enable({ emitEvent: false });
    }
  }

  private _onLoanProgramCodeSelectionChange(flag: boolean, loanProgramCode: string): void {
    this.loanProgramOptions$.pipe(take(1)).subscribe((loanProgramOptions) => {
      if (!!loanProgramCode && loanProgramCode !== 'Select' && !!loanProgramOptions) {
        const loanProgram = loanProgramOptions.find((program) => program.loanProgramCode === loanProgramCode);
        const amortizationType = loanProgram ? loanProgram.loanProductType : 0,
          interestOnlyPeriod = loanProgram ? loanProgram.interestOnlyPeriod : null,
          isJumbo = loanProgram ? (loanProgram.isJumbo === 1 ? true : false) : false,
          loanProgramName = loanProgram ? loanProgram.loanProgramName : '',
          balloonTerm = loanProgram ? loanProgram.balloonTerm : null;

        applyTransaction(() => {
          this.mortgageTypeForm.get('armQualRateOption').reset();
          this.mortgageTypeForm.get('intOnlyQualPaymentCalcOption').reset();
          this.mortgageTypeForm.get('isNegativeAmortization').reset();
          this.mortgageTypeForm.get('isGrantLoan').reset();
          this._updateStoreAndForm(StoreProperty.loanProgramName, loanProgramName);
          this._updateStoreAndForm(StoreProperty.amortizationTypeDescription, loanProgramName);
          this._updateStoreAndForm(StoreProperty.amortizationType, amortizationType);
          this._updateStoreAndForm(StoreProperty.interestOnlyPeriod, interestOnlyPeriod);
          this._updateStoreAndForm(StoreProperty.isJumbo, isJumbo);
          this._updateStoreAndForm(StoreProperty.isInterestOnly, !!interestOnlyPeriod);
          this._updateStoreAndForm(StoreProperty.balloonTerm, balloonTerm);

          if (this._hasLoanProgramCodeForIRRR()) {
            this._updateStoreAndForm(StoreProperty.fundingFeeType, FundingFeeType.interestRateReductionRefi);
          }

          if (!flag) {
            const term = loanProgram ? loanProgram.term : null;
            this._updateStoreAndForm(StoreProperty.loanTerm, term);
          }

          this._mapArmProgramValues(loanProgram);

          if (this._isConstructionPermLoanProgram(loanProgramCode)) {
            this._updateForConstruction();
          }

          if (this.mortgageTypeForm.controls.mortgageAppliedFor.value === MortgageType.notAssigned) {
            this.mortgageTypeForm.controls.mortgageAppliedFor.setValue(loanProgram.mortgageType, { emitEvent: false });

            // Filter the dropdowns for Loan Program Code / Loan Program Description to only contain values relevant for selected Mortgage Type
            this._setObservableLoanPrograms(this._originationChannel);
          }
        });

        this._checkBondLoanProgramName(loanProgramName);
      }
    });
  }

  private _onLoanProgramNameSelectionChange(flag: boolean, loanProgramName: string): void {
    this.loanProgramOptions$.pipe(take(1)).subscribe((loanProgramOptions) => {
      if (!!loanProgramName && loanProgramName !== 'Select' && !!loanProgramOptions) {
        const loanProgram = loanProgramOptions.find((program) => program.loanProgramName === loanProgramName);
        const amortizationType = loanProgram ? loanProgram.loanProductType : 0,
          interestOnlyPeriod = loanProgram ? loanProgram.interestOnlyPeriod : null,
          isJumbo = loanProgram ? (loanProgram.isJumbo === 1 ? true : false) : false,
          loanProgramCode = loanProgram ? loanProgram.loanProgramCode : '',
          balloonTerm = loanProgram ? loanProgram.balloonTerm : null;

        applyTransaction(() => {
          this.mortgageTypeForm.get('armQualRateOption').reset();
          this.mortgageTypeForm.get('intOnlyQualPaymentCalcOption').reset();
          this.mortgageTypeForm.get('isNegativeAmortization').reset();
          this.mortgageTypeForm.get('isGrantLoan').reset();
          this._updateStoreAndForm(StoreProperty.loanProgramCode, loanProgramCode);
          this._updateStoreAndForm(StoreProperty.amortizationTypeDescription, loanProgramName);
          this._updateStoreAndForm(StoreProperty.amortizationType, amortizationType);
          this._updateStoreAndForm(StoreProperty.interestOnlyPeriod, interestOnlyPeriod);
          this._updateStoreAndForm(StoreProperty.isJumbo, isJumbo);
          this._updateStoreAndForm(StoreProperty.isInterestOnly, !!interestOnlyPeriod);
          this._updateStoreAndForm(StoreProperty.balloonTerm, balloonTerm);

          if (this._hasLoanProgramCodeForIRRR()) {
            this._updateStoreAndForm(StoreProperty.fundingFeeType, FundingFeeType.interestRateReductionRefi);
          }

          if (!flag) {
            const term = loanProgram ? loanProgram.term : null;
            this._updateStoreAndForm(StoreProperty.loanTerm, term);
          }

          this._mapArmProgramValues(loanProgram);

          if (this.mortgageTypeForm.controls.mortgageAppliedFor.value === MortgageType.notAssigned) {
            this.mortgageTypeForm.controls.mortgageAppliedFor.setValue(loanProgram.mortgageType, { emitEvent: false });

            // Filter the dropdowns for Loan Program Code / Loan Program Description to only contain values relevant for selected Mortgage Type
            this._setObservableLoanPrograms(this._originationChannel);
          }
        });
        this._checkBondLoanProgramName(loanProgramName);
      }
    });
  }

  private _populatePurposeOfLoanDropdown(mortgageType: MortgageType) {
    if (mortgageType === MortgageType.va || mortgageType === MortgageType.usda) {
      this.purposeOfLoanOptions = purposeOfLoanOptions.filter(
        (x) =>
          x.value === PurposeOfLoan.select ||
          x.value === PurposeOfLoan.purchase ||
          x.value === PurposeOfLoan.refinance ||
          x.value === PurposeOfLoan.constructionPerm,
      );
    } else if (mortgageType === MortgageType.fha) {
      this.purposeOfLoanOptions = purposeOfLoanOptions.filter((x) => x.value !== PurposeOfLoan.construction);
    } else {
      this.purposeOfLoanOptions = purposeOfLoanOptions;
    }
  }

  private _formatDateFields(loanInfo: LoanDataModel) {
    loanInfo.armIndexValueForWeekEnding = formatDatePCalendar(this.loanInfo.armIndexValueForWeekEnding);
  }

  private _save(isGvtFundingFactor = false): void {
    const mortgageTypeRaw = this.mortgageTypeForm.getRawValue();

    // TODO(?): Subscribe to changes to subOrdinateFinance that happen outside of this component (e.g. Expenses affecting Subordinate Finance). Then, update this.loanInfo accordingly.
    // Current implementation below is the simpler but less robust approach.
    const subOrdinateFinanceCurrent = this._loanQuery.getValue().subOrdinateFinance;
    //subOrdinateFinanceCurrent.helocMaxBalance = this.mortgageTypeForm.get('helocMaxBalance').value;
    this.loanInfo = { ...this.loanInfo, ...mortgageTypeRaw, subOrdinateFinance: subOrdinateFinanceCurrent };
    this._calculateDownPayment();

    //  Run logic with updated loanInfo
    this._loanStore.update({
      ...this.loanInfo,
    });
    this._fileDataStore.update({ waiveEscrow: mortgageTypeRaw.waiveEscrow, positiveRentalHistory: mortgageTypeRaw.positiveRentalHistory });
    this._propertyStore.update({ landLoanStatus: mortgageTypeRaw?.landLoanStatus });
    this._populateFundingFeeDropdown();

    /**
     * Update Store first with raw values then make calculations
     * that then update the store with individual properties with
     * method _updateStoreAndForm. Ran into issue where the this.loanInfo
     * would have the getRawValues overwritten before saving to store because
     * of the method _updateStoreAndForm sets this.loanInfo with the values in store.
     * Therefore, we should update store with raw values then calculate which will then
     * update the individual properties accordingly.
     */
    //  TODO don't run calculations all the time on all form change?
    if (isGvtFundingFactor) {
      this._calculateLoanGvtFundingFactors(this.loanInfo);
    } else {
      this._calculateLoan(this.loanInfo);
    }
    this._disableControls();
  }

  private _setMIFields(mortgageType: MortgageType): void {
    applyTransaction(() => {
      this._updateStoreAndForm(StoreProperty.monthlyMiAmount, null);
      this._updateStoreAndForm(StoreProperty.upfrontMiAmount, null);
      this._updateStoreAndForm(StoreProperty.monthlyMiFactor, null);
      this._updateStoreAndForm(StoreProperty.upfrontMiFactor, null);
      this._updateStoreAndForm(StoreProperty.miPaidInCash, null);
      this._updateStoreAndForm(StoreProperty.mipffFinancingCalcOption, this._getValueOfUpfrontMIType(mortgageType));
    });
  }

  //  Code from Clear 1. Modified to act like Clear 1 in Clear 2
  private readonly _getValueOfUpfrontMIType = (mortgageType: MortgageType): UpfrontMiType => {
    if (mortgageType === MortgageType.va && this.loanInfo.fundingFeeType !== FundingFeeType.exempt) {
      /**
       * Clear 1 code logic has it so it will always be financed
       * if statement doesn't work as intended in Clear 1
       */
      //if (this.loanInfo.upfrontMiType !== UpfrontMiType.paidInCash) {
      return UpfrontMiType.financed;
      //}
    } else if (
      !!this.loanInfo.upfrontMiType &&
      (mortgageType === MortgageType.conventional || mortgageType === MortgageType.other || mortgageType === MortgageType.heloc)
    ) {
      return this.loanInfo.mipffFinancingCalcOption as UpfrontMiType;
    } else {
      this.loanInfo.mipffFinancingCalcOption = UpfrontMiType.financed;
    }
  };

  private _setupObservables(setHead = true): void {
    combineLatest({
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      statusRequiresFullDisabling: this._storeQuery.statusRequiresFullDisabling$(),
      loanStatus: this._sessionQuery.select((s) => s.loanStatusId),
      rateLockStatus: this._sessionQuery.select((s) => s.rateLockStatus),
      syncType: this._fileDataQuery.select((f) => f.syncType),
      mortgageType: this._loanQuery.select((l) => l.mortgageAppliedFor),
      loanStatusHistory: this._cmg1003LoanStatusHistoryQuery.select((l) => l),
    })
      .pipe(
        filter(({ all1003Loaded }) => {
          return all1003Loaded;
        }),
        tap((loanData) => {
          if (loanData.loanStatus === LoanStatus.CreditOnly || loanData.loanStatus === LoanStatus.PreApproved) {
            this.mortgageTypeForm.get('loanPurpose').disable({ emitEvent: false });
          } else if (loanData.loanStatusHistory?.preapprovalApplicationDate || loanData.loanStatusHistory?.creditOnlyDate) {
            this.mortgageTypeForm.get('loanPurpose').disable({ emitEvent: false });
          }
        }),
        map(({ statusRequiresFullDisabling, loanStatus, rateLockStatus, syncType, mortgageType }) => {
          return (
            statusRequiresFullDisabling ||
            (rateLockStatus === LockStatusType.Locked && loanStatus === LoanStatus.LePending) ||
            ((syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien) &&
              (mortgageType === MortgageType.fha || mortgageType === MortgageType.va || mortgageType === MortgageType.usda))
          );
        }),
        distinctUntilChanged(),
        takeUntil(this._destroyed$),
      )
      .subscribe((isFullyDisabled) => {
        this._isFullyDisabled = isFullyDisabled;
        if (this._isFullyDisabled) {
          const controlsToPreserve = ['refinancePurposeVa', 'refinancePurposeFha', 'refinancePurposeAu'];

          Object.keys(this.mortgageTypeForm.controls).forEach((key) => {
            if (!controlsToPreserve.includes(key)) {
              this.mortgageTypeForm.get(key).disable({ emitEvent: false });
            }
          });
        }
      });

    const refiFields = ['refinancePurposeVa', 'refinancePurposeFha', 'refinancePurposeAu'];
    refiFields.forEach((fieldName) => {
      this.mortgageTypeForm
        .get(fieldName)
        .valueChanges.pipe(
          takeUntil(this._destroyed$),
          filter((value) => !!value && value !== 0),
        )
        .subscribe(() => {
          this._cmg1003Service.refinanceTypeChanged$.next(true);
        });
    });

    /**
     * Using observables over input
     * ngOnChanges caused unneeded firing of events
     * Subscribing gives us more control of events
     */
    this._sessionQuery
      .select((state) => state.all1003Loaded)
      .pipe(
        takeUntil(this._destroyed$),
        filter((loaded) => loaded),
        switchMap(() =>
          this._loanQuery.selectLoanInfo().pipe(
            withLatestFrom(this._fileDataQuery.select('waiveEscrow'), this._fileDataQuery.select('positiveRentalHistory')),
            take(1),
            skipWhile((data) => !data[0] && !this.mortgageTypeForm),
          ),
        ),
      )
      .subscribe(([loan, waiveEscrow, positiveRentalHistory]) => {
        if (loan && this.mortgageTypeForm) {
          this.loanInfo = Object.assign({}, loan);
          this._cdRef.detectChanges();
          this._populatePurposeOfRefinanceDropdown(1, this.loanInfo.loanPurpose, this.loanInfo.mortgageAppliedFor);
          this._populatePurposeOfLoanDropdown(this.loanInfo.mortgageAppliedFor);
          if (isGvtLoan(this.loanInfo.mortgageAppliedFor)) {
            this._calculateLoanGvtFundingFactors(this.loanInfo);
          } else {
            this._calculateLoan(this.loanInfo);
          }
          this._calculateLoan(this.loanInfo);
          this._formatDateFields(this.loanInfo);

          if (setHead) {
            setTimeout(() => {
              this._loanQuery.dirtyChecker2.dirtyChecker.setHead();
            }, 0);
          }

          this.mortgageTypeForm.patchValue(this.loanInfo, { emitEvent: false });
          this.mortgageTypeForm.patchValue(
            { fhaExpectedEndorsementDate: this.loanInfo.fhaExpectedEndorsementDate ? new Date(this.loanInfo.fhaExpectedEndorsementDate) : null },
            { emitEvent: false },
          );
          this.mortgageTypeForm.patchValue(
            { fhaPreviousEndorsementDate: this.loanInfo.fhaPreviousEndorsementDate ? new Date(this.loanInfo.fhaPreviousEndorsementDate) : null },
            { emitEvent: false },
          );
          this.mortgageTypeForm.patchValue({ waiveEscrow, maxLoanLimitByCounty: this.loanInfo.maxLoanLimitByCounty }, { emitEvent: false });
          this._prevPmiTypeValue = this.mortgageTypeForm.get('pmiType').value;

          this._loanQuery.isFha$.pipe(takeUntil(this._destroyed$)).subscribe((isFha) => {
            if (isFha) {
              this.maxBaseLoanAmount = this.loanInfo.maxLoanLimitByCounty;
            } else {
              this.maxBaseLoanAmount = maxCurrencyValue;
            }
          });

          if (positiveRentalHistory === 0) {
            positiveRentalHistory = 2; // Set No as default
          }
          this.mortgageTypeForm.patchValue({ positiveRentalHistory }, { emitEvent: false });

          this._originationChannel = loan.originationChannel;
          this._setObservableLoanPrograms(this._originationChannel);

          //  Needs to runto initilize initial state TODO find better way
          this._disableControls();

          this.originalLoanProgram = {
            loanProgramCode: loan.loanProgramCode,
            loanProgramName: loan.loanProgramName,
          };
          this._isHomeFundItLoan = loan.isHomeFundItLoan;
          this._checkBondLoanProgramName(loan.loanProgramName);

          this.clearLoanProgramError();
          this._setMiPaidInCash();
          this.handleAppraisalValueWarning();
        }
      });

    // [URLA2-1376] - TEMPORARY: Remove this._interceptLoanProgramChange() later.
    this.mortgageTypeForm
      .get('loanProgramCode')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((value) => {
        this._interceptLoanProgramChange('loanProgramCode', value, () => {
          this._onLoanProgramCodeSelectionChange(false, value);
          this._onChangeLoanProgram();
        });
      });

    this.mortgageTypeForm
      .get('loanProgramName')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((value) => {
        this._interceptLoanProgramChange('loanProgramName', value, () => {
          this._onLoanProgramNameSelectionChange(false, value);
          this._onChangeLoanProgram();
        });
      });

    this.mortgageTypeForm
      .get('loanPurpose')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((value) => {
        // Should update the value of loanPurpose in the Store here
        // Since right after this function, this.loanInfo.loanPurpose
        this._updateStoreAndForm(StoreProperty.loanPurpose, value);
        this._populatePurposeOfRefinanceDropdown(0, value, this.loanInfo.mortgageAppliedFor);
        this._populateConstructionPermChanges(0, value);
      });

    this.mortgageTypeForm
      .get('fundingFeeType')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        this._setByteDefaultValue();
      });

    this.mortgageTypeForm
      .get('caseNumberAssignedDate')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        this._setByteDefaultValue();
      });

    this.mortgageTypeForm
      .get('interestRate')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((value) => {
        this._updateStoreAndForm(StoreProperty.qualRate, this._calculateQualRate(value));
      });

    this.mortgageTypeForm
      .get('armQualRateOption')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        this._updateStoreAndForm(StoreProperty.qualRate, this._calculateQualRate(null));
      });

    this.mortgageTypeForm
      .get('isHomeFundItLoan')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((value) => {
        this._isHomeFundItLoan = value;
      });

    /**
     * _populatePurposeOfRefinanceDropdown relies on the following properties to
     * populate the FundingFeeDropdown correctly
     * TODO Look into optimizing this
     */
    combineLatest([
      this._loanQuery.select((loan) => loan.areYouDisabledVeteran),
      this._loanQuery.select((loan) => loan.homeOwnedThroughVa),
      this._loanQuery.select((loan) => loan.refinancePurposeAu),
      this._loanQuery.select((loan) => loan.refinancePurposeVa),
      this._loanQuery.select((loan) => loan.refinancePurposeFha),
      /**
       * _populatePurposeOfRefinanceDropdown also calls this._populateFundingFeeDropdown();
       * which when loanPurpose changes it calls _populatePurposeOfRefinanceDropdown
       */
      //
      this._loanQuery.select((loan) => loan.loanPurpose),
    ])
      .pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        this._populateFundingFeeDropdown();
      });

    // Enable, disable, and/or clear out Loan Program dropdowns, when "Mortgage Type" changes.
    this.mortgageTypeForm
      .get('mortgageAppliedFor')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((value: MortgageType) => {
        const loanProgramCodeControl = this.mortgageTypeForm.get('loanProgramCode');
        const loanProgramNameControl = this.mortgageTypeForm.get('loanProgramName');
        const amortizationTypeControl = this.mortgageTypeForm.get('amortizationType');
        const amortizationTypeDescControl = this.mortgageTypeForm.get('amortizationTypeDescription');

        this.loanProgramOptions$
          .pipe(
            take(1),
            tap((loanPrograms) => {
              const matchingLoanProgram = loanPrograms.find((lp) => lp.mortgageType === value && lp.loanProgramCode === loanProgramCodeControl.value);

              if (!matchingLoanProgram || value === MortgageType.notAssigned) {
                // .reset() to clear out previous errors on these form controls.
                loanProgramCodeControl.reset('', { emitEvent: false });
                loanProgramNameControl.reset('', { emitEvent: false });
                amortizationTypeControl.reset('', { emitEvent: false });
                amortizationTypeDescControl.reset('', { emitEvent: false });
                this.mortgageTypeForm.get('balloonTerm').setValue(null, { emitEvent: false });

                this.clearLoanProgramError();
              }
            }),
          )
          .subscribe();

        loanProgramCodeControl.enable({ emitEvent: false });
        loanProgramNameControl.enable({ emitEvent: false });

        this._initialize(value);
        this._handleMaxCountyLoanLimit(value);

        if (value !== MortgageType.fha) {
          this.mortgageTypeForm.get('positiveRentalHistory').setValue(this.positiveRentalHistoryOptions[1].value, { emitEvent: false });
        }
        this._checkBondLoanProgramName(loanProgramNameControl.value);
      });

    // Monitor Construction Field related changes as these can also be updated in the Property Section
    this._loanQuery
      .select((loan) => loan.constructionPurpose)
      .pipe(distinctUntilChanged())
      .subscribe((constructionPurpose) => {
        this.mortgageTypeForm.patchValue({ constructionPurpose }, { emitEvent: false });
      });
    this._propertyQuery
      .select((property) => property.landLoanStatus)
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe((landLoanStatus) => {
        this.mortgageTypeForm.patchValue({ landLoanStatus }, { emitEvent: false });
      });

    this._updateSharedLoanProperties();

    this.mortgageTypeForm.valueChanges
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged(), debounceTime(0))
      .subscribe((mortgageTypeFormValueChanges) => {
        this._save(isGvtLoan(mortgageTypeFormValueChanges.mortgageAppliedFor));
      });

    this._handlePositiveRentalHistoryTooltip();

    this._loanQuery
      .select([(t) => t.ltv, (t) => t.mortgageAppliedFor, (t) => t.loanProgramCode])
      .pipe(takeUntil(this._destroyed$))
      .subscribe(([ltv, mortgageAppliedFor, loanProgramCode]) => {
        this.isMiQuoteDisabled = this._miQuoteService.isMiQuoteDisabled(ltv, mortgageAppliedFor, loanProgramCode);
        if (!this.isMiQuoteDisabled) {
          this.mortgageTypeForm.get('pmiType').markAsTouched({ onlySelf: true });
        }
        this.mortgageTypeForm.get('pmiType').updateValueAndValidity({ onlySelf: true });
      });

    combineLatest({
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      mortgageType: this._loanQuery.select((l) => l.mortgageAppliedFor),
      loanProgramCode: this._loanQuery.select((l) => l.loanProgramCode),
    })
      .pipe(
        filter(({ all1003Loaded }) => {
          return all1003Loaded;
        }),
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
      )
      .subscribe((data) => {
        this.is2ndG01LoanProgram$.next(data.mortgageType === MortgageType.heloc && data.loanProgramCode?.startsWith('2nd G01'));

        if (this.is2ndG01LoanProgram$.value) {
          this.mortgageTypeForm.controls.minimumDrawAmount.addValidators(Validators.required);
        } else {
          this.mortgageTypeForm.controls.minimumDrawAmount.removeValidators(Validators.required);
          this.mortgageTypeForm.controls.minimumDrawAmount.setValue(null, { emitEvent: false });
          this.mortgageTypeForm.controls.aioInitialDrawAmount.setValue(null, { emitEvent: false });
        }

        this.mortgageTypeForm.controls.minimumDrawAmount.updateValueAndValidity({ emitEvent: false });
      });

    combineLatest({
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      state: this._propertyQuery.select((p) => p.state),
      loanPurpose: this._loanQuery.select((l) => l.loanPurpose),
      texasA6Status: this._loanQuery.select((l) => l.texasA6Status),
    })
      .pipe(
        filter(({ all1003Loaded }) => {
          return all1003Loaded;
        }),
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
      )
      .subscribe((data) => {
        if (data.state?.toUpperCase() === 'TX') {
          this._isTexasState = true;
        } else if (this._isTexasState) {
          this._updateStoreAndForm(StoreProperty.texasA6Status, 0, false);
          this._isTexasState = false;
        }

        this._texasA6Required(data.texasA6Status, data.loanPurpose);
      });

    this._sessionQuery
      .select((s) => s.loanStatusId)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((loanStatus) => {
        const statusesToEnableMinimumDrawAmount = [LoanStatus.Prospect, LoanStatus.LePending, LoanStatus.CreditOnly, LoanStatus.PreApproved];
        this.disableMinimumDrawAmount$.next(!statusesToEnableMinimumDrawAmount.includes(loanStatus));
      });
  }

  private _texasA6Required(texasA6Status: number, loanPurpose: number) {
    const ctrl = this.mortgageTypeForm.get('texasA6Status');
    if (this._isTexasState && loanPurpose === LoanPurposeType.refinance) {
      this.showTexasA6Status = true;

      if (!ctrl.hasValidator(CmgValidators.required(0))) {
        ctrl.addValidators(CmgValidators.required(0));
      } else {
        ctrl.clearValidators();
      }

      this.mortgageTypeForm.patchValue({ texasA6Status: texasA6Status }, { emitEvent: false });
    } else {
      this.showTexasA6Status = false;
      ctrl.clearValidators();
      ctrl.setValue(null, { emitEvent: false });
    }

    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private _handlePositiveRentalHistoryTooltip() {
    this.isFha$.pipe(takeUntil(this._destroyed$)).subscribe((value) => {
      this.positiveRentalHistoryTooltipText = !value ? 'This selection is only available for FHA Loans' : '';
    });
  }

  private _handleMaxCountyLoanLimit(currentMortgageType: MortgageType) {
    const baseLoanAmount = this.mortgageTypeForm.controls.baseLoanAmount.value;
    const maxCountyLoanLimit = this.loanInfo.maxLoanLimitByCounty;

    if (currentMortgageType === MortgageType.fha && baseLoanAmount > maxCountyLoanLimit) {
      this.mortgageTypeForm.controls.baseLoanAmount.setValue(maxCountyLoanLimit, { emitEvent: false });
      this.mortgageTypeForm.controls.dpfactor.setValue(DpFactorType.select);
      this._messageService.add({
        severity: 'warn',
        summary: '',
        detail:
          'Base Loan Amount has been updated to Max County Loan Limit $' +
          this.loanInfo.maxLoanLimitByCounty +
          ' due to the loan being changed to FHA. Please make sure to update the rest of your file accordingly to accommodate this change.',
        key: 'loan-summary',
      });
    }
  }

  private _handlePmiTypeChange() {
    this._miFactorsDefaultValueAssignment();
  }

  /**
   * [Auto-Recovery Flow]:
   * 1) Wait for user's decision on auto-recovery modal.
   * 2) User selects "Unsaved Changes" on auto-recovery modal.
   * 3) Loan store gets updated, via _update1003ForAutoRecovery() in cmg-1003.service.ts.
   * 4) Observable fires (ONCE) to refresh this component's subscriptions and form with latest data.
   */
  private _awaitAutoRecoveryDecision() {
    combineLatest([this._sessionQuery.select((session) => session.needsAutoRecoveryDecision), this._loanQuery.dirtyChecker2.isDirty$])
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filter(([needsDecision, isDirty]) => canUseUnsavedChanges(this._sessionStore.getValue(), isDirty)),
        take(1),
        tap(() => {
          if (this._sessionQuery.getValue().cached1003?.snapshot?.Value) {
            this._setupObservables(false);
          }
        }),
      )
      .subscribe();
  }

  private _updateSharedLoanProperties(): void {
    this._loanQuery
      .select((loan) => loan.loanPurpose)
      .pipe(
        takeUntil(this._destroyed$),
        /**
         * Check that local component property loanInfo doesn't match
         * value that was changed outside of component or when local component property
         * loanInfo was changed within component in _updateStoreAndForm
         */
        filter((value) => value && this.loanInfo.loanPurpose && value !== this.loanInfo.loanPurpose),
      )
      .subscribe((value) => {
        //  We need to emit true so that code runs based on change of loanPurpose
        this._updateStoreAndForm(StoreProperty.loanPurpose, value, true);
      });
  }

  private _updateStoreAndForm(update: StoreProperty, value: number | string | boolean, emit = false) {
    if (typeof value === 'number' && isNaN(value as number)) {
      value = 0;
    }
    switch (update) {
      case StoreProperty.qualRate:
        this._loanStore.update({ qualRate: value as number });
        this.mortgageTypeForm.get('qualRate').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.monthlyMiAmount:
        this._loanStore.update({ monthlyMiAmount: value as number });
        this.mortgageTypeForm.get('monthlyMiAmount').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.upfrontMiAmount:
        this._loanStore.update({ upfrontMiAmount: value as number });
        this.mortgageTypeForm.get('upfrontMiAmount').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.upfrontMiFactor:
        this._loanStore.update({ upfrontMiFactor: value as number });
        this.mortgageTypeForm.get('upfrontMiFactor').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.totalLoanAmount:
        this._loanStore.update({ totalLoanAmount: value as number });
        this.mortgageTypeForm.get('totalLoanAmount').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.monthlyMiFactor:
        this._loanStore.update({ monthlyMiFactor: value as number });
        this.mortgageTypeForm.get('monthlyMiFactor').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.loanProgramName:
        this._loanStore.update({ loanProgramName: value as string });
        this.mortgageTypeForm.get('loanProgramName').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.amortizationType:
        this._loanStore.update({ amortizationType: value as number });
        this.mortgageTypeForm.get('amortizationType').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.isInterestOnly:
        this._loanStore.update({ isInterestOnly: value as boolean });
        break;
      case StoreProperty.interestOnlyPeriod:
        this._loanStore.update({ interestOnlyPeriod: value as number });
        this.mortgageTypeForm.get('interestOnlyPeriod').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.isJumbo:
        this._loanStore.update({ isJumbo: value as boolean });
        this.mortgageTypeForm.get('isJumbo').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.fundingFeeType:
        this._loanStore.update({ fundingFeeType: value as number });
        this.mortgageTypeForm.get('fundingFeeType').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.loanTerm:
        this._loanStore.update({ loanTerm: value as number });
        this.mortgageTypeForm.get('loanTerm').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.loanProgramCode:
        this._loanStore.update({ loanProgramCode: value as string });
        this.mortgageTypeForm.get('loanProgramCode').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.caseNumberAssignedDate:
        this._loanStore.update({ caseNumberAssignedDate: value as number });
        this.mortgageTypeForm.get('caseNumberAssignedDate').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.rhsCaseNoAssignmentPeriod:
        this._loanStore.update({ gusFirstRunDate: value as number });
        this.mortgageTypeForm.get('gusFirstRunDate').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.pmiType:
        this._loanStore.update({ pmiType: value as number });
        this.mortgageTypeForm.get('pmiType').patchValue(value, { emitEvent: emit });
        this._prevPmiTypeValue = value as number;
        break;
      case StoreProperty.mipffFinancingCalcOption:
        this._loanStore.update({ mipffFinancingCalcOption: value as number });
        this.mortgageTypeForm.get('mipffFinancingCalcOption').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.miPaidInCash:
        this._loanStore.update({ miPaidInCash: value as number });
        this.mortgageTypeForm.get('miPaidInCash').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.interestRate:
        this._loanStore.update({ interestRate: value as number });
        this.mortgageTypeForm.get('interestRate').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.baseLoanAmount:
        this._loanStore.update({ baseLoanAmount: value as number });
        this.mortgageTypeForm.get('baseLoanAmount').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.amortizationTypeDescription:
        this._loanStore.update({ amortizationTypeDescription: value as string });
        this.mortgageTypeForm.get('amortizationTypeDescription').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.loanPurpose:
        this._loanStore.update({ loanPurpose: value as number });
        this.mortgageTypeForm.get('loanPurpose').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.armQualRateOption:
        this._loanStore.update({ armQualRateOption: value as number });
        this.mortgageTypeForm.get('armQualRateOption').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.intOnlyQualPaymentCalcOption:
        this._loanStore.update({ intOnlyQualPaymentCalcOption: value as number });
        this.mortgageTypeForm.get('intOnlyQualPaymentCalcOption').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.balloonTerm:
        this._loanStore.update({ balloonTerm: isNullish(value) ? null : (value as number) });
        this.mortgageTypeForm.get('balloonTerm').patchValue(value, { emitEvent: emit });
        break;
      case StoreProperty.texasA6Status:
        this._loanStore.update({ texasA6Status: value as number });
        this.mortgageTypeForm.get('texasA6Status').patchValue(value, { emitEvent: emit });
        break;
      default:
        break;
    }
    this.loanInfo = this._loanQuery.getValue();
    this._disableControls();
  }

  private readonly _miFactorsDefaultValueAssignment = (): void => {
    this._setMIFields(this.loanInfo.mortgageAppliedFor);
    this.monthlyMIFactorDefault = null;
    this.upfrontMIAmountDefault = null;
    this.upfrontMIFactorDefault = null;
    this._setByteDefaultValue();
  };

  private readonly _setByteDefaultValue = (): void => {
    applyTransaction(() => {
      this._updateStoreAndForm(StoreProperty.monthlyMiAmount, this.monthlyMIFactorDefault);
      this._updateStoreAndForm(StoreProperty.upfrontMiAmount, this.upfrontMIAmountDefault);
      this._updateStoreAndForm(StoreProperty.upfrontMiFactor, this.upfrontMIFactorDefault);
      this._updateStoreAndForm(StoreProperty.mipffFinancingCalcOption, UpfrontMiType.financed);
    });
    this._setMiPaidInCash();
  };

  private readonly _setMiPaidInCash = (): void => {
    if (this.loanInfo.mipffFinancingCalcOption === UpfrontMiType.notfinanced) {
      applyTransaction(() => {
        this._updateStoreAndForm(StoreProperty.miPaidInCash, this.loanInfo.upfrontMiAmount);
        this._updateStoreAndForm(StoreProperty.totalLoanAmount, this.loanInfo.baseLoanAmount);
      });
    }
  };

  private readonly _populatePurposeOfRefinanceDropdown = (id: number, loanPurposeType: LoanPurposeType, mortgageType: MortgageType): void => {
    this.refinancePurposeOptions = [];
    if (loanPurposeType === LoanPurposeType.refinance) {
      if (mortgageType === MortgageType.fha) {
        this.refinancePurposeOptions = fhaRefinancePurposeOptions;
      } else if (mortgageType === MortgageType.va) {
        this.refinancePurposeOptions = vaRefinancePurposeOptions;
      } else {
        this.refinancePurposeOptions = otherRefinancePurposeOptions;
      }
    }
    this._populateFundingFeeDropdown();
  };

  private readonly _populateConstructionPermChanges = (id: number, loanPurposeType: LoanPurposeType): void => {
    if (loanPurposeType === LoanPurposeType.constructionPerm) {
      const loanProgramCodeControl = this.mortgageTypeForm.get('loanProgramCode');
      const loanProgramNameControl = this.mortgageTypeForm.get('loanProgramName');
      const amortizationTypeControl = this.mortgageTypeForm.get('amortizationType');
      const amortizationTypeDescControl = this.mortgageTypeForm.get('amortizationTypeDescription');
      this.mortgageTypeForm.get('mortgageAppliedFor').setValue(MortgageType.conventional, { emitEvent: true });
      loanProgramCodeControl.reset('', { emitEvent: false });
      loanProgramNameControl.reset('', { emitEvent: false });
      amortizationTypeControl.reset('', { emitEvent: false });
      amortizationTypeDescControl.reset('', { emitEvent: false });
    } else {
      // this needs to be done to trigger the changes related to mortgagetype selection change
      const value = this.mortgageTypeForm.get('mortgageAppliedFor').value;
      this.mortgageTypeForm.get('mortgageAppliedFor').setValue(value);
    }

    this.clearLoanProgramError();
  };

  private readonly _updateForConstruction = (): void => {
    const loanProgramCode: string = this.mortgageTypeForm.get('loanProgramCode').value;
    if (this._isConstructionLoanProgram(loanProgramCode)) {
      this._updateStoreAndForm(StoreProperty.loanPurpose, LoanPurposeType.construction, false);
      this._updateStoreAndForm(StoreProperty.armQualRateOption, ARMQualRateOption.NoteRate);
      this._updateStoreAndForm(StoreProperty.intOnlyQualPaymentCalcOption, IntOnlyQualPaymentCalcOption.AmortizingOverFullTerm);
      this._updateStoreAndForm(StoreProperty.qualRate, this._calculateQualRate());
    } else if (this._isConstructionPermLoanProgram(loanProgramCode)) {
      this._updateStoreAndForm(StoreProperty.loanPurpose, LoanPurposeType.constructionPerm, false);
    }
  };

  private readonly _isConstructionLoanProgram = (loanProgramCode: string): boolean => {
    return loanProgramCode && loanProgramCode.toLowerCase().includes('c2x');
  };

  private readonly _isConstructionPermLoanProgram = (loanProgramCode: string): boolean => {
    const mortgageType = this.mortgageTypeForm.get('mortgageAppliedFor').value;
    return loanProgramCode && loanProgramCode.endsWith('CP') && mortgageType === MortgageType.conventional;
  };

  private readonly _populateFundingFeeDropdown = (): void => {
    this.fundingFeeOptions = fundingFeeTypeOptions;
    if (this.loanInfo.mortgageAppliedFor === MortgageType.va && this.loanInfo.loanPurpose === LoanPurposeType.refinance) {
      if (this.loanInfo.refinancePurposeVa === RefiTypeVA.IRRR) {
        const options = [FundingFeeType.select, FundingFeeType.interestRateReductionRefi, FundingFeeType.exempt];
        this.fundingFeeOptions = this.fundingFeeOptions.filter((value) => options.includes(value.id));
      } else if (this.loanInfo.refinancePurposeVa === RefiTypeVA.other) {
        if (this.loanInfo.areYouDisabledVeteran) {
          this.fundingFeeOptions = this.fundingFeeOptions.filter((value) => value.id === FundingFeeType.exempt);
        } else {
          if (this.loanInfo.homeOwnedThroughVa) {
            const options = [FundingFeeType.select, FundingFeeType.regularMilitarySubequentUse, FundingFeeType.reservesOrGuardSubsequentUse];
            this.fundingFeeOptions = this.fundingFeeOptions.filter((value) => options.includes(value.id));
          } else {
            const options = [
              FundingFeeType.select,
              FundingFeeType.regularMilitaryFirstUse,
              FundingFeeType.reservesOrGuardFirstUse,
              FundingFeeType.regularMilitarySubequentUse,
              FundingFeeType.reservesOrGuardSubsequentUse,
              FundingFeeType.manufacturedHome,
              FundingFeeType.loanAssumption,
              FundingFeeType.exempt,
            ];
            this.fundingFeeOptions = this.fundingFeeOptions.filter((value) => options.includes(value.id));
          }
        }
      } else {
        //  If select is selected for Refinance Type then we should have an empty list with only select
        const options = [FundingFeeType.select];
        this.fundingFeeOptions = this.fundingFeeOptions.filter((value) => options.includes(value.id));
      }
    }
    if (
      this.loanInfo.mortgageAppliedFor === MortgageType.va &&
      (this.loanInfo.loanPurpose === LoanPurposeType.purchase || this.loanInfo.loanPurpose === LoanPurposeType.constructionPerm)
    ) {
      this.fundingFeeOptions = this.fundingFeeOptions.filter((option) => option.id !== FundingFeeType.interestRateReductionRefi);
    }
    if (this.loanInfo.fundingFeeType) {
      const fundingFeeType = this.loanInfo.fundingFeeType;

      this._updateStoreAndForm(StoreProperty.fundingFeeType, fundingFeeType);
      if (this._hasLoanProgramCodeForIRRR()) {
        this._updateStoreAndForm(StoreProperty.fundingFeeType, FundingFeeType.interestRateReductionRefi);
      }
    }
  };

  private _setObservableLoanPrograms(originationChannel: OriginationChannel) {
    this.loanProgramOptions$ = this.mortgageTypeForm.get('mortgageAppliedFor').valueChanges.pipe(
      takeUntil(this._destroyed$),
      startWith(this.loanInfo.mortgageAppliedFor),
      shareReplay({ refCount: false, bufferSize: 1 }),
      switchMap(() => {
        return this._lookupQuery
          .select((state) => state.loanPrograms)
          .pipe(
            takeUntil(this._destroyed$),
            switchMap((loanPrograms) => {
              if (loanPrograms) {
                return of([
                  { loanProgramCode: 'Select', loanProgramName: 'Select' } as LoanProgram,
                  ...this._assembleLoanProgramOptions(loanPrograms, originationChannel),
                ]);
              }
              return EMPTY;
            }),
          );
      }),
    );

    //map loanProgramOptions to use a seperate label and value
    this.loanProgramCodeOptions$ = combineLatest({
      loanProgramOptions: this.loanProgramOptions$,
      isClear2DisabledForLoan: this._storeQuery.isClear2DisabledForLoan$,
    }).pipe(
      map(({ loanProgramOptions, isClear2DisabledForLoan }) => {
        const mappedOptions = loanProgramOptions.map((t) => ({
          value: t.loanProgramCode === 'Select' ? '' : t.loanProgramCode,
          label: t.loanProgramCode,
        }));

        return this._filterFlexHelocPrograms(mappedOptions, isClear2DisabledForLoan);
      }),
      shareReplay(1),
    );

    this.loanProgramNameOptions$ = combineLatest({
      loanProgramOptions: this.loanProgramOptions$,
      isClear2DisabledForLoan: this._storeQuery.isClear2DisabledForLoan$,
    }).pipe(
      map(({ loanProgramOptions, isClear2DisabledForLoan }) => {
        const mappedOptions = loanProgramOptions.map((t) => ({
          value: t.loanProgramName === 'Select' ? '' : t.loanProgramName,
          label: t.loanProgramName,
        }));

        return this._filterFlexHelocPrograms(mappedOptions, isClear2DisabledForLoan);
      }),
      shareReplay(1),
    );
  }

  /* Filter Flex Heloc options that user should not select in clear2 */
  private _filterFlexHelocPrograms<T extends { value: string; label: string }>(options: T[], isClear2DisabledForLoan: boolean): T[] {
    if (!isClear2DisabledForLoan) {
      return options.filter(
        (program) =>
          !program.value.includes('Flex HELOC') &&
          !program.value.includes('Brokered - NFTYHEL') &&
          !program.value.includes('NFTYDoor Standalone Home Equity Loan'),
      );
    }
    return options;
  }

  private _setMortgageAppliedForDisabledState(): void {
    this._ausQuery.isPendingAny$.pipe(takeUntil(this._destroyed$), distinctUntilChanged()).subscribe((isDisabled) => {
      isDisabled
        ? this.mortgageTypeForm.controls.mortgageAppliedFor.disable({ emitEvent: false })
        : this.mortgageTypeForm.controls.mortgageAppliedFor.enable({ emitEvent: false });
    });
  }

  /**
   * Assembles the Loan Program options for the dropdowns.
   * Filtered by Origination Channel, Mortgage Type, and not disabled.
   * Sorted by `loanProgramCode`.
   */
  private _assembleLoanProgramOptions(loanPrograms: LoanProgram[], originationChannel: OriginationChannel) {
    // TODO: Determine if API can provide these codes dynamically, instead of hard-coding here.
    // The channel codes are calculated by adding all the origination channels.
    // Retail = 1, BrokeredOut = 2, Wholesale = 4, correspondent(Purchased) = 8, MiniCorr = 16
    const retailChannelCodes = [0, 1, 3, 5, 7, 9, 11, 13, 15, 29, 31];
    const brokeredChannelCodes = [0, 2, 3, 6, 7, 15, 31];
    const mortgageTypeSelection = this.mortgageTypeForm.controls.mortgageAppliedFor.value;

    const assembledLoanPrograms = loanPrograms
      .filter(
        (x) =>
          !x.isDisabled &&
          (mortgageTypeSelection === MortgageType.notAssigned || x.mortgageType === mortgageTypeSelection) &&
          (originationChannel === OriginationChannel.retail ? retailChannelCodes : brokeredChannelCodes).includes(x.originationChannels),
      )
      .sort((a, b) => a.loanProgramCode.localeCompare(b.loanProgramCode));

    const loanProgram = assembledLoanPrograms.find((program) => program.loanProgramCode === this.originalLoanProgram?.loanProgramCode);

    if (!loanProgram && this.originalLoanProgram?.loanProgramCode !== '') {
      assembledLoanPrograms.push(this.originalLoanProgram);
    }

    this.loanProgramOptions = assembledLoanPrograms;

    return assembledLoanPrograms;
  }

  private _mapArmProgramValues(program: LoanProgram): void {
    const amortizationType = program ? program.loanProductType : 0;
    const isHelocAio = program.mortgageType === MortgageType.heloc && program.loanProgramCode?.startsWith('801');
    const isHelocArm = program.mortgageType === MortgageType.heloc && !program.loanProgramCode?.startsWith('801');
    const isConstruction =
      program.mortgageType === MortgageType.conventional &&
      (this._isConstructionLoanProgram(program.loanProgramCode) || this._isConstructionPermLoanProgram(program.loanProgramCode));

    if (amortizationType !== AmortizationType.ARM) {
      return;
    }

    const armFieldUpdates = {};
    armFieldUpdates['armAddFeatures'] = program.armAddFeatures;
    armFieldUpdates['armConversionOption'] = program.armConversionOption;
    armFieldUpdates['armCommonRenewal'] = program.armCommonRenewal;
    armFieldUpdates['armAdjustCapFirst'] = program.armAdjustCapFirst;
    armFieldUpdates['armIntRateFixedFor'] = program.armIntRateFixedFor;
    armFieldUpdates['armFloor'] = program.armFloor;
    armFieldUpdates['armIndexValue'] = program.armIndexValue;
    armFieldUpdates['armIndexNameOV'] = program.armIndexNameOV;
    armFieldUpdates['armIndexType'] = program.armIndexType; //
    armFieldUpdates['armIndexForWeekEnding'] = program.armIndexForWeekEnding;
    armFieldUpdates['armIndexValueForWeekEnding'] = formatDatePCalendar(program.armIndexForWeekEnding);
    armFieldUpdates['armCarryover'] = program.armCarryover;
    armFieldUpdates['armHasNegAmFeature'] = program.armHasNegAmFeature;
    armFieldUpdates['armLookbackDays'] = program.armLookbackDays;
    armFieldUpdates['armNegAmDesc'] = program.armNegAmDesc;
    armFieldUpdates['armAdjustCapSubsequent'] = program.armAdjustCapSubsequent;
    armFieldUpdates['armIntRateAdjustAt'] = program.armIntRateAdjustAt;
    armFieldUpdates['armRounding'] = program.armRounding;
    armFieldUpdates['demandFeature'] = program.demandFeature;

    // HELOC AIO Program Updates
    if (isHelocAio) {
      armFieldUpdates['armIndexPublished'] = program.armIndexPublished;
      armFieldUpdates['unusedFundsLeftOnCreditLine'] =
        checkIsNAN(this.loanInfo?.subOrdinateFinance.helocMaxBalance) - checkIsNAN(this.loanInfo.aioInitialDrawAmount);
      armFieldUpdates['aioMaxRate'] = checkIsNAN(this.loanInfo?.interestRate) + checkIsNAN(program.armLifeCap);
      armFieldUpdates['maxDrawAmountNinetyNinePercentOfLoanAmount'] = checkIsNAN(this.loanInfo?.totalLoanAmount) * 0.99;
      armFieldUpdates['armLifeCap'] = program.armLifeCap;
      armFieldUpdates['armNoAdjCap'] = program.armNoAdjCap;

      if (program.loanProgramCode?.includes('3FIX') && !aioStates.some((e) => e === this._loanApplicationQuery.getValue().propertyState)) {
        //AIO Loan Program includes "3FIX" AND the Subject Property is NOT in one of the following states, IL, MD, MN, NM, or PA
        armFieldUpdates['armQualRateOption'] = ARMQualRateOption.NoteRate;
        armFieldUpdates['intOnlyQualPaymentCalcOption'] = IntOnlyQualPaymentCalcOption.AmortizingOverFullTerm;
        this.mortgageTypeForm.get('armQualRateOption').patchValue(ARMQualRateOption.NoteRate, { emitEvent: false });
        this.mortgageTypeForm
          .get('intOnlyQualPaymentCalcOption')
          .patchValue(IntOnlyQualPaymentCalcOption.AmortizingOverFullTerm, { emitEvent: false });
      } else {
        //AIO Loan Program does not include "3FIX" or  the Subject Property is in one of the following states, IL, MD, MN, NM, or PA
        armFieldUpdates['armQualRateOption'] = ARMQualRateOption.RateOrFIR;
        armFieldUpdates['intOnlyQualPaymentCalcOption'] = IntOnlyQualPaymentCalcOption.AmortizingOverFullTerm;
        this.mortgageTypeForm.get('armQualRateOption').patchValue(ARMQualRateOption.RateOrFIR, { emitEvent: false });
        this.mortgageTypeForm
          .get('intOnlyQualPaymentCalcOption')
          .patchValue(IntOnlyQualPaymentCalcOption.AmortizingOverFullTerm, { emitEvent: false });
      }
    }

    // HELOC ARM Program Updates
    if (isHelocArm) {
      armFieldUpdates['armLifeCap'] = program.armLifeCap;
    }

    // Other ARM Program Updates
    if (!(program.mortgageType === MortgageType.heloc) && !isConstruction) {
      armFieldUpdates['armMargin'] = program.armMargin;
    }

    this.mortgageTypeForm.patchValue(armFieldUpdates, { emitEvent: false });
    this._loanStore.update(armFieldUpdates);
  }

  private _calculateQualRate(interestRate?: number): number {
    let qualRate = 0;
    if (!interestRate) {
      interestRate = this.mortgageTypeForm.get('interestRate').value;
      qualRate = interestRate;
    }
    const programCode = this.mortgageTypeForm.get('loanProgramCode').value;
    const qualRateOption = this.mortgageTypeForm.get('armQualRateOption').value;
    if (programCode && qualRateOption) {
      const program = this.loanProgramOptions.find((program) => program.loanProgramCode === programCode);
      qualRate = Math.max(Number(checkIsNAN(interestRate)), Number(checkIsNAN(program.armIndexValue)) + Number(checkIsNAN(program.armMargin)));
      switch (qualRateOption) {
        case ARMQualRateOption.NoteRate:
        case ARMQualRateOption.MaxRateInFirst5Years:
          qualRate = Number(checkIsNAN(interestRate));
          break;
        case ARMQualRateOption.RatePlus2OrFIR:
          qualRate = Math.max(
            Number(checkIsNAN(interestRate)) + 2,
            Number(checkIsNAN(program.armIndexValue)) + Number(checkIsNAN(program.armMargin)),
          );
          break;
        case ARMQualRateOption.RatePlus1:
          qualRate = Number(checkIsNAN(interestRate)) + 1;
          break;
        case ARMQualRateOption.RatePlus2OrRoundedFIR:
          qualRate = Math.max(
            Number(checkIsNAN(interestRate)) + 2,
            Number(checkIsNAN(program.armIndexValue)) + Number(checkIsNAN(program.armMargin)),
          );
          qualRate = roundToValue(qualRate, 1 / 8); // default rounding is 1/8th
          break;
        case ARMQualRateOption.RateOrRoundedFIR:
          qualRate = Math.max(Number(checkIsNAN(interestRate)), Number(checkIsNAN(program.armIndexValue)) + Number(checkIsNAN(program.armMargin)));
          qualRate = roundToValue(qualRate, 1 / 8); // default rounding is 1/8th
          break;
        default:
          break;
      }
    }
    if (!qualRate) {
      interestRate = this.mortgageTypeForm.get('interestRate').value;
      qualRate = interestRate;
    }
    qualRate = formatNumber(qualRate, 3);
    return qualRate;
  }

  /** Prevents user from selecting unsupported Loan Programs - AIO, ARM, HELOC, Construction. */
  private _interceptLoanProgramChange(loanProgramKey: 'loanProgramCode' | 'loanProgramName', loanProgramValue: string, callbackFn?: () => void) {
    this.loanProgramOptions$
      .pipe(
        take(1),
        tap((loanPrograms) => {
          const matchingLoanProgram: LoanProgram = loanPrograms.find((x) => x[loanProgramKey] === loanProgramValue);

          if (matchingLoanProgram) {
            this.clearLoanProgramError();

            if (callbackFn) {
              callbackFn();
            }
          } else {
            // FLOW:
            // 0. `this._supportedLoanProgramValidator()` fires => "Loan Program Code" / "Loan Program Description" will show red error state.

            // 1. Clear out "Loan Program Code" and "Loan Program Descriptions".
            // Use { emitEvent: false } to avoid risk of infinite loop.
            this.mortgageTypeForm.get('loanProgramCode').setValue(null, { emitEvent: false });
            this.mortgageTypeForm.get('loanProgramName').setValue(null, { emitEvent: false });
            this.mortgageTypeForm.get('loanProgramCode').markAsPristine();
            this.mortgageTypeForm.get('loanProgramName').markAsPristine();

            if (
              this.mortgageTypeForm.get('loanProgramCode')?.errors?.cmgValidationError ||
              this.mortgageTypeForm.get('loanProgramName')?.errors?.cmgValidationError
            ) {
              // 2. Show red error banner in "Loan Information" section.
              if (!this.loanProgramError.showingBanner) {
                this._messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail:
                    'Construction loan programs are not yet supported in Clear 2.0. To select one of these loan programs, please first select the loan program in Clear 1.0 or Byte, and then you can return to Clear 2.0 after saving.',
                  key: this.loanProgramError.messageServiceKey,
                });

                this.loanProgramError.showingBanner = true;
              }
            }
          }
        }),
      )
      .subscribe();
  }

  private _supportedLoanProgramValidator(loanProgramKey: 'loanProgramCode' | 'loanProgramName', control: AbstractControl) {
    // [Scenario]: Initialization.
    if (this.loanProgramOptions?.length === 0) {
      return true;
    }

    const matchingLoanProgram = this.loanProgramOptions.find((x) => x[loanProgramKey] === control.value);

    // Treat '' and 'Select' as valid within _supportedLoanProgramValidator(), so ONLY "Required" error message is shown.
    const isValidSelection = !!matchingLoanProgram || ['', 'Select', null].includes(this.mortgageTypeForm.get(loanProgramKey).value);

    const matchesOriginal = this.originalLoanProgram[loanProgramKey] === control.value;

    // [Scenario]: Loan Program is unsupported, but it was already selected (via Clear 1.0 or Byte) AND user did NOT change it.
    if (!isValidSelection && matchesOriginal) {
      return true;
    }

    // [Scenario]: User selected a new Loan Program.
    return isValidSelection;
  }

  private _calculateDownPayment(): void {
    const priceValue =
      this.loanInfo.appraisalValue < this.loanInfo.salesPrice && this.loanInfo.appraisalValue
        ? this.loanInfo.appraisalValue
        : this.loanInfo.salesPrice;

    if (priceValue) {
      if (this.loanInfo.dpfactor === DpFactorType.downpaymentPercentage) {
        this.loanInfo.downPaymentAmount = priceValue * (this.loanInfo.downPaymentPercentage / 100);
        this.mortgageTypeForm.get('downPaymentAmount').setValue(this.loanInfo.downPaymentAmount, { emitEvent: false });
      } else if (this.loanInfo.dpfactor === DpFactorType.downpaymentAmount) {
        this.loanInfo.downPaymentPercentage = (this.loanInfo.downPaymentAmount / priceValue) * 100;
        this.mortgageTypeForm.get('downPaymentPercentage').setValue(this.loanInfo.downPaymentPercentage, { emitEvent: false });
      }
    }
  }
}
